import React, { useEffect, useRef, useState } from "react";

// Tags substituídas no backend ([empresa]→nome da empresa, etc.). Mesmo conjunto dos Modelos.
export const TAGS = ["[empresa]", "[vaga]", "[candidato]", "[titulo]", "[prazo]", "[link_resposta]"];
export const EMOJIS_WPP = ["👤","🏢","✅","🎯","💡","📈","📉","⏰","💬","⭐","👉","🔔","🌱","🚀","📝","📍","💼","🙌","😊","🙏","❌","👻","🤖","📲","🔒","💪","🎉","⚠️","📅","🤝"];

// Editor visual (WYSIWYG) — sem dependência externa (contentEditable + execCommand). Mesmo dos Modelos de e-mail.
// Alterna entre modo visual e código HTML. Botões de tag inserem no cursor.
export function HtmlEditor({ initialHtml, onChange, tags = [] }) {
  const ref = useRef(null);
  const [codigo, setCodigo] = useState(false);
  const [html, setHtml] = useState(initialHtml || "");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- só re-sincroniza ao alternar visual/código; reagir a `html` resetaria o cursor
  useEffect(() => { if (!codigo && ref.current) ref.current.innerHTML = html || ""; }, [codigo]);

  // O conteúdo costuma chegar DEPOIS da montagem (vem de uma chamada à API). Sem esta sincronização o
  // editor ficava com o texto antigo — e salvar por cima gravava o conteúdo errado, apagando links e
  // formatação de um modelo que estava certo no banco. Só reescreve quando a fonte realmente mudou, para
  // não mexer no cursor de quem está digitando.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const novo = initialHtml || "";
    if (novo === html) return;
    setHtml(novo);
    if (!codigo && ref.current) ref.current.innerHTML = novo;
  }, [initialHtml]);

  const emit = (h) => { setHtml(h); onChange(h); };
  const fromDiv = () => emit(ref.current?.innerHTML || "");
  const cmd = (c, v) => { ref.current?.focus(); document.execCommand(c, false, v); fromDiv(); };
  const link = () => { const u = window.prompt("URL do link (https://...):"); if (u) cmd("createLink", u); };
  const img = () => { const u = window.prompt("URL da imagem (https://...):"); if (u) cmd("insertImage", u); };
  const insert = (t) => { ref.current?.focus(); document.execCommand("insertText", false, t); fromDiv(); };

  const Btn = ({ on, children, title }) => (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={on}
      className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-100">{children}</button>
  );

  return (
    <div className="rounded-lg border border-gray-300 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 p-1.5">
        {!codigo && <>
          <Btn on={() => cmd("bold")} title="Negrito"><b>B</b></Btn>
          <Btn on={() => cmd("italic")} title="Itálico"><i>I</i></Btn>
          <Btn on={() => cmd("underline")} title="Sublinhado"><u>U</u></Btn>
          <Btn on={() => cmd("formatBlock", "h3")} title="Título">H</Btn>
          <Btn on={() => cmd("insertUnorderedList")} title="Lista">• Lista</Btn>
          <span className="mx-1 h-4 border-l border-gray-300" />
          {/* Alinhamento — o que faltava para montar o texto como no Word. Vale em e-mail: o execCommand
              grava text-align inline, que é o único jeito que Gmail e Outlook respeitam. */}
          <Btn on={() => cmd("justifyLeft")} title="Alinhar à esquerda">⯇</Btn>
          <Btn on={() => cmd("justifyCenter")} title="Centralizar">≡</Btn>
          <Btn on={() => cmd("justifyRight")} title="Alinhar à direita">⯈</Btn>
          <Btn on={() => cmd("justifyFull")} title="Justificar">☰</Btn>
          <Btn on={() => cmd("insertHorizontalRule")} title="Linha divisória">—</Btn>
          <span className="mx-1 h-4 border-l border-gray-300" />
          <Btn on={link} title="Inserir link">🔗 Link</Btn>
          <Btn on={img} title="Inserir imagem">🖼️ Imagem</Btn>
          <Btn on={() => cmd("removeFormat")} title="Limpar formatação">✕</Btn>
          {tags.length > 0 && <span className="mx-1 h-4 border-l border-gray-300" />}
          {tags.map((t) => <Btn key={t} on={() => insert(t)} title={`Inserir ${t}`}>{t}</Btn>)}
        </>}
        <button type="button" onClick={() => setCodigo((c) => !c)}
          className="ml-auto rounded border border-gray-300 bg-white px-2 py-1 text-xs font-mono hover:bg-gray-100">{codigo ? "👁 Visual" : "</> HTML"}</button>
      </div>
      {codigo ? (
        <textarea value={html} onChange={(e) => emit(e.target.value)} rows="10"
          className="w-full p-3 text-sm font-mono outline-none" placeholder="<p>HTML aqui...</p>" />
      ) : (
        <div ref={ref} contentEditable suppressContentEditableWarning onInput={fromDiv}
          className="min-h-[160px] p-3 text-sm outline-none" style={{ lineHeight: 1.5 }} />
      )}
    </div>
  );
}

// Editor de WhatsApp — texto puro com marcadores nativos (*negrito*, _itálico_, ~tachado~) + emojis + tags.
// Mesma barra dos Modelos de WhatsApp. Controlado por `value`/`onChange`.
export function WhatsappEditor({ value, onChange, tags = [], rows = 6, placeholder }) {
  const taRef = useRef(null);
  const [emojiAberto, setEmojiAberto] = useState(false);

  const envolver = (marca) => {
    const ta = taRef.current;
    const val = value || "";
    const s = ta?.selectionStart ?? val.length;
    const e = ta?.selectionEnd ?? s;
    const sel = val.slice(s, e);
    const novo = val.slice(0, s) + marca + sel + marca + val.slice(e);
    onChange(novo);
    setTimeout(() => { if (!ta) return; ta.focus(); const pos = sel ? e + marca.length * 2 : s + marca.length; ta.selectionStart = ta.selectionEnd = pos; }, 0);
  };
  const inserir = (t) => {
    const ta = taRef.current;
    const val = value || "";
    const s = ta?.selectionStart ?? val.length;
    const e = ta?.selectionEnd ?? s;
    const novo = val.slice(0, s) + t + val.slice(e);
    onChange(novo);
    setTimeout(() => { if (!ta) return; ta.focus(); ta.selectionStart = ta.selectionEnd = s + t.length; }, 0);
  };

  const btn = "rounded-md bg-white border border-gray-300 text-gray-700 text-xs px-2 py-1 hover:bg-gray-100";
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-1">
        <button type="button" title="Negrito (*texto*)" onClick={() => envolver("*")} className={`${btn} font-bold`}>N</button>
        <button type="button" title="Itálico (_texto_)" onClick={() => envolver("_")} className={`${btn} italic`}>I</button>
        <button type="button" title="Tachado (~texto~)" onClick={() => envolver("~")} className={`${btn} line-through`}>S</button>
        <span className="relative">
          <button type="button" title="Emojis" onClick={() => setEmojiAberto((a) => !a)} className={btn}>😀 ▾</button>
          {emojiAberto && (
            <span className="absolute z-20 top-full left-0 mt-1 grid grid-cols-8 gap-0.5 bg-white border border-gray-300 rounded-lg shadow-lg p-2 w-64">
              {EMOJIS_WPP.map((em) => (
                <button key={em} type="button" onClick={() => { inserir(em); setEmojiAberto(false); }}
                  className="text-lg rounded hover:bg-gray-100 leading-none p-1">{em}</button>
              ))}
            </span>
          )}
        </span>
        {tags.length > 0 && <span className="mx-1 w-px h-4 bg-gray-200" />}
        {tags.map((t) => (
          <button key={t} type="button" onClick={() => inserir(t)}
            className="rounded-md bg-white border border-gray-300 text-gray-700 text-xs font-mono px-2 py-1 hover:bg-primary hover:text-white">{t}</button>
        ))}
      </div>
      <textarea ref={taRef} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono leading-relaxed outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
}
